import { forwardRef, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Calendar } from "lucide-react";
import {
  formatLocalDate,
  getHistoryDatesInRange,
  getMonthQueryRange,
} from "../../api/apiTaskManagerHistory";
import "./HistoryDatePicker.scss";

const HistoryDatePickerTrigger = forwardRef(function HistoryDatePickerTrigger(
  { value, onClick },
  ref
) {
  return (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      className="history-datepicker-trigger"
      aria-label={value ? `Selected date ${value}` : "Select date"}
    >
      <Calendar
        className="history-datepicker-trigger__icon"
        strokeWidth={1.75}
        aria-hidden="true"
      />
      <span className="history-datepicker-trigger__value">{value}</span>
    </button>
  );
});

function HistoryDatePicker({ selected, onChange }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [visibleMonth, setVisibleMonth] = useState(
    () => selected || new Date()
  );

  const { startDate, endDate } = useMemo(
    () => getMonthQueryRange(visibleMonth),
    [visibleMonth]
  );

  const { data: datesWithRecords = [] } = useQuery({
    queryKey: ["taskManager_history_dates", startDate, endDate],
    queryFn: () => getHistoryDatesInRange(startDate, endDate),
    staleTime: 60_000,
  });

  const recordDateSet = useMemo(
    () => new Set(datesWithRecords),
    [datesWithRecords]
  );

  const handleMonthChange = (monthDate) => {
    setVisibleMonth(monthDate);
  };

  const handleChange = (nextDate) => {
    if (!nextDate) return;
    setVisibleMonth(nextDate);
    onChange?.(nextDate);
  };

  return (
    <div className="history-date-picker">
      <DatePicker
        selected={selected}
        onChange={handleChange}
        onMonthChange={handleMonthChange}
        onCalendarOpen={() => {
          setVisibleMonth(selected || new Date());
          // Only blur on mobile so the iOS accessory bar dismisses.
          if (isMobile && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }}
        dateFormat="yyyy/MM/dd"
        calendarStartDay={1}
        fixedHeight
        shouldCloseOnSelect
        withPortal={isMobile}
        // Critical: only use portalId on mobile. On desktop, portalId still
        // portals the popper into #history-datepicker-portal which had
        // pointer-events:none and blocked real date clicks.
        portalId={isMobile ? "history-datepicker-portal" : undefined}
        showPopperArrow={false}
        popperPlacement="bottom-end"
        popperClassName="history-datepicker-popper"
        calendarClassName="history-datepicker-calendar"
        wrapperClassName="history-datepicker-wrapper"
        customInput={<HistoryDatePickerTrigger />}
        dayClassName={(date) =>
          recordDateSet.has(formatLocalDate(date))
            ? "history-datepicker-day--has-record"
            : undefined
        }
        renderDayContents={(day, date) => (
          <span className="history-datepicker-day">
            <span className="history-datepicker-day__num">{day}</span>
            {recordDateSet.has(formatLocalDate(date)) && (
              <span className="history-datepicker-day__dot" aria-hidden="true" />
            )}
          </span>
        )}
        renderCustomHeader={({
          monthDate,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <div className="history-datepicker-header">
            <button
              type="button"
              className="history-datepicker-nav"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="history-datepicker-title">
              {monthDate.toLocaleString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              type="button"
              className="history-datepicker-nav"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
        )}
      />
    </div>
  );
}

export default HistoryDatePicker;
